import { NestedFilterInput, NestedFilterItem } from '@apeiron/common'
import { Box, styled, Typography } from '@mui/material'
import ItemFilterList from '@src/components/share/filter/nestedFilter/NestedFilterList'
import ItemFilterPopup from '@src/components/share/filter/nestedFilter/NestedFilterPopup'
import * as R from 'ramda'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SubTitle = styled(Typography)`
  color: #333333;
  font-size: 14px;
  font-weight: 300;
  padding-left: 8px;
`

const NestedFilter = <
  T1 extends NestedFilterInput,
  T2 extends NestedFilterInput
>(
  props: Props<T1, T2>
) => {
  const {
    allItems,
    className,
    defaultSearchParam,
    field,
    filterInput,
    searchPlaceholder,
    additionalComponentInPopup,
    onFilterInputChange,
    renderActionItems = () => <></>,
    renderItem,
    renderPopupFilter = () => <></>,
    searchByText = () => '',
    searchInnerItems = () => [],
    searchItems,
    sortItems = list => list,
    selectedItems,
    setSelectedItems,
    title
  } = props

  const visibleItems: NestedFilterItem[] = useMemo(() => {
    const items = searchItems(filterInput)

    return R.map(item => {
      return {
        ...item,
        disabled: false
      }
    }, items)
  }, [filterInput, searchItems])

  useEffect(() => {
    if (R.isEmpty(selectedItems) && !R.isEmpty(visibleItems)) {
      const newSelected = R.filter((item: NestedFilterItem) => {
        const keys = R.pluck('uniqueKey', selectedItems)

        return !R.includes(item.uniqueKey, keys)
      }, visibleItems)

      const sortedItems = sortItems(
        R.concat(selectedItems, newSelected)
      ) as NestedFilterItem[]

      setSelectedItems(sortedItems)
    }
  }, [visibleItems])

  const handleOnRemoveItem = useCallback(
    (item: NestedFilterItem) => {
      const filter = R.reject<string, string[]>(
        filterUniqueKey => item.uniqueKey === String(filterUniqueKey),
        R.propOr([], field, filterInput)
      )

      setSelectedItems(
        R.reject(
          (selected: NestedFilterItem) => selected.uniqueKey === item.uniqueKey,
          selectedItems
        )
      )

      onFilterInputChange({
        ...filterInput,
        [field]: filter
      })
    },
    [field, filterInput, onFilterInputChange, selectedItems]
  )

  const handleOnToggleItem = useCallback(
    (item: NestedFilterItem) => {
      item.disabled = !item.disabled

      setSelectedItems(
        R.map((selected: NestedFilterItem) => {
          if (selected.uniqueKey === item.uniqueKey) {
            return item
          }

          return selected
        }, selectedItems)
      )

      const { uniqueKey } = item

      const filter = !item.disabled
        ? R.append(uniqueKey, R.propOr([], field, filterInput))
        : R.reject<string, string[]>(
            filterUniqueKey => uniqueKey === String(filterUniqueKey),
            R.propOr([], field, filterInput)
          )

      onFilterInputChange({ ...filterInput, [field]: filter })
    },
    [field, filterInput, onFilterInputChange, selectedItems]
  )

  const handleOnChange = useCallback(
    (items: NestedFilterItem[]) => {
      const dedupedItems = R.unionWith(
        R.eqBy(R.propOr('', 'uniqueKey')),
        items,
        selectedItems
      )

      const newSelectedItems = R.map(
        (item: NestedFilterItem): NestedFilterItem => {
          return {
            ...item,
            disabled: false
          }
        },
        dedupedItems
      )

      const sortedItems = sortItems(newSelectedItems)

      setSelectedItems(sortedItems)

      const newVisibleItems = R.filter((item: NestedFilterItem) => {
        return !item.disabled
      }, sortedItems)

      const fieldValue = R.pluck('uniqueKey', newVisibleItems)

      const filter = {
        [field]: fieldValue
      }

      onFilterInputChange({
        ...filterInput,
        ...filter
      })
    },
    [field, filterInput, onFilterInputChange, selectedItems, sortItems]
  )

  const handleOnRenderPopupItem = useCallback(
    (item: NestedFilterItem) => {
      return renderItem(item, true)
    },
    [renderItem]
  )

  const handleOnRenderListItem = useCallback(
    (item: NestedFilterItem) => {
      return renderItem(item, false)
    },
    [renderItem]
  )

  return (
    <Container className={className}>
      {title && <SubTitle>{title}</SubTitle>}
      <ItemFilterPopup
        title={title}
        allItems={allItems}
        defaultSearchParam={defaultSearchParam}
        items={selectedItems}
        searchPlaceholder={searchPlaceholder}
        onChange={handleOnChange}
        renderItem={handleOnRenderPopupItem}
        renderPopupFilter={renderPopupFilter}
        searchByText={searchByText}
        searchItems={searchInnerItems}
        additionalComponentInPopup={additionalComponentInPopup}
      />
      <ItemFilterList
        items={selectedItems}
        removeItem={handleOnRemoveItem}
        renderActionItems={renderActionItems}
        renderItem={handleOnRenderListItem}
        toggleItem={handleOnToggleItem}
      />
    </Container>
  )
}

type Props<T1 extends NestedFilterInput, T2 extends NestedFilterInput> = {
  additionalComponentInPopup?: ReactElement
  allItems: NestedFilterItem[]
  className?: string
  defaultSearchParam: T2
  field: string
  filterInput: T1
  searchPlaceholder: string
  onFilterInputChange: (input: T1) => void
  renderActionItems?: (item: NestedFilterItem) => ReactElement
  renderItem: (item: NestedFilterItem, popup: boolean) => ReactElement
  renderPopupFilter?: (
    filterInput: T2,
    onFilterItem: (filterInput: T2) => void
  ) => ReactElement
  searchByText?: (item: NestedFilterItem) => string
  searchItems: (params: T1) => NestedFilterItem[]
  searchInnerItems?: (params: T2) => NestedFilterItem[]
  sortItems?: (items: NestedFilterItem[]) => NestedFilterItem[]
  selectedItems: NestedFilterItem[]
  setSelectedItems: (items: NestedFilterItem[]) => void
  title?: string
}

export default NestedFilter
